import { fmtMoney, fmtPct, fmtDate } from "../parsers/xmlParser";
import { STATUS_STYLE, CATEGORY_STYLE } from "../utils/fieldMaps";

/* ── InfoRow ──────────────────────────────────────────────── */
export function InfoRow({ label, value, bold, color, money, pct }) {
  let display = value;
  if (money && typeof value === "number") display = fmtMoney(value);
  if (pct   && typeof value === "number") display = fmtPct(value);
  if (!display && display !== 0) return null;

  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "baseline",
      padding: "5px 0", borderBottom: "1px solid #f1f5f9", gap: 8,
    }}>
      <span style={{ fontSize: 12, color: "#64748b", flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: 14, fontWeight: bold ? 700 : 500,
        color: color || "#1e293b", textAlign: "left",
      }}>{display}</span>
    </div>
  );
}

/* ── SectionTitle ─────────────────────────────────────────── */
export function SectionTitle({ children, color }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: "0.8px", color: color || "#6366f1",
      margin: "18px 0 8px", paddingBottom: 4,
      borderBottom: `1.5px solid ${color || "#6366f1"}30`,
    }}>
      {children}
    </div>
  );
}

/* ── StatusBadge ──────────────────────────────────────────── */
export function StatusBadge({ label }) {
  const s = STATUS_STYLE[label] || STATUS_STYLE.default;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      color: s.color, background: s.bg,
      padding: "2px 8px", borderRadius: 20,
    }}>{label}</span>
  );
}

/* ── CategoryBadge ────────────────────────────────────────── */
export function CategoryBadge({ cat }) {
  const cs = CATEGORY_STYLE[cat] || CATEGORY_STYLE["ביטוח מנהלים"];
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      color: cs.accent, background: cs.light,
      padding: "2px 8px", borderRadius: 20,
    }}>{cat}</span>
  );
}

/* ── CompanyLogo ──────────────────────────────────────────── */
const COMPANY_LOGOS = [
  { key: "אלטשולר", src: "/logos/altshuler.png" },
  { key: "ילין", src: "/logos/yelin.png" },
  { key: "מנורה", src: "/logos/menora.jpg" },
  { key: "הראל", src: "/logos/harel.png" },
  { key: "כלל", src: "/logos/clal.jpg" },
  { key: "מגדל", src: "/logos/migdal.png" },
  { key: "הפניקס", src: "/logos/phoenix.png" },
  { key: "הכשרה", src: "/logos/hachshara.png" },
  { key: "מיטב", src: "/logos/meitav.png" },
  { key: "מור", src: "/logos/more.jpg" },
  { key: "אנליסט", src: "/logos/analyst.jpg" },
  { key: "איילון", src: "/logos/ayalon.png" },
  { key: "אינפיניטי", src: "/logos/infinity.jpg" },
  { key: "aig", src: "/logos/aig.png" },
  { key: "ibi", src: "/logos/ibi.png" },
  { key: "אי.בי.אי", src: "/logos/ibi.png" },
];

export function CompanyLogo({ name }) {
  if (!name) return null;
  const lowerName = name.toLowerCase();
  const match = COMPANY_LOGOS.find(c => lowerName.includes(c.key));
  if (match) {
    return (
      <img
        src={match.src}
        alt={name}
        title={name}
        style={{ maxHeight: 24, maxWidth: 50, objectFit: "contain", borderRadius: 4, verticalAlign: "middle" }}
      />
    );
  }
  return <span>{name}</span>;
}

/* ── Chip ─────────────────────────────────────────────────── */
export function Chip({ label, value, bg, border, color }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{
      background: bg, border: `1px solid ${border}`,
      borderRadius: 7, padding: "3px 9px", display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
    }}>
      <div style={{ fontSize: 10, color: "#64748b" }}>{label}:</div>
      <div style={{ fontSize: 12, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

/* ── AlertBanner ──────────────────────────────────────────── */
export function AlertBanner({ icon, text, color, bg, border }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "8px 12px", borderRadius: 8,
      background: bg, border: `1px solid ${border}`,
      fontSize: 12, color, marginTop: 8,
    }}>
      <span>{icon}</span>
      <span>{text}</span>
    </div>
  );
}

/* ── SummaryCard ──────────────────────────────────────────── */
export function SummaryCard({ label, value, sub, borderColor, onClick, active, condensed }) {
  return (
    <div onClick={onClick} style={{
      position: "relative",
      background: active ? "#eff6ff" : "white",
      border: `1.5px solid ${active ? borderColor : "#e2e8f0"}`,
      borderRadius: 12,
      padding: onClick
        ? (condensed ? "10px 12px 28px" : "14px 16px 28px")
        : (condensed ? "10px 12px" : "14px 16px"),
      borderRight: `4px solid ${borderColor}`,
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.15s",
      minWidth: condensed ? "160px" : "200px",
    }}>
      <div style={{ fontSize: condensed ? 14 : 17, color: "#64748b", marginBottom: condensed ? 3 : 6, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: condensed ? 20 : 28, fontWeight: 800, color: "#0f172a", lineHeight: 1, fontFamily: "'Inter', sans-serif", letterSpacing: "-0.5px" }}>{value}</div>
      {sub && <div style={{ fontSize: condensed ? 11 : 13, color: "#94a3b8", marginTop: condensed ? 3 : 4 }}>{sub}</div>}
      {onClick && (
        <div style={{
          position: "absolute", bottom: 8, left: 10,
          fontSize: 10, fontWeight: 700, color: borderColor,
          background: `${borderColor}14`, border: `1px solid ${borderColor}55`,
          borderRadius: 6, padding: "2px 7px", lineHeight: 1.4,
          display: "inline-flex", alignItems: "center",
        }} title="לחץ לפירוט מלא">פירוט ›</div>
      )}
    </div>
  );
}
